import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { apiClient } from '../../api/client';
import { sendExpose, getExposePreview, ExposePreview, getSendAccounts, SendAccount } from '../../api/settings';
import { Contact, AcquisitionProfile } from '../../types';
import { MatchCommunicationTab } from './MatchCommunicationTab';
import { CompanyDetail } from '../companies/CompanyDetail';

type ModalTab = 'overview' | 'stammdaten' | 'communication';

interface CompanyFull {
  id: string;
  display_name: string;
  legal_name?: string | null;
  company_type?: string | null;
  website?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country_code?: string | null;
  description?: string | null;
  verified_at?: string | null;
  approved_at?: string | null;
  contacts: (Contact & { email?: string | null })[];
  profiles: AcquisitionProfile[];
}

interface Props {
  companyId: string | null;
  /** Profile-ID die den Match ausgelöst hat — wenn null/undefined wird "irgendein Profil freigegeben" akzeptiert. */
  matchProfileId?: string | null;
  mandateId: string;
  mandateTitle: string;
  initialTab?: ModalTab;
  onClose: () => void;
  onSent?: () => void;
}

function formatVolume(min?: number | null, max?: number | null): string {
  if (!min && !max) return '—';
  const fmt = (v: number) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(0)} Mio.`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)} Tsd.`;
    return `${v}`;
  };
  if (min && max) return `${fmt(min)} – ${fmt(max)} €`;
  if (min) return `ab ${fmt(min)} €`;
  return `bis ${fmt(max!)} €`;
}

const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 500, textTransform: 'uppercase',
  letterSpacing: '0.6px', color: 'var(--text3)', marginBottom: '3px',
};
const valStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--text)' };

export function InvestorDetailModal({ companyId, matchProfileId, mandateId, mandateTitle, initialTab, onClose, onSent }: Props) {
  const [company, setCompany] = useState<CompanyFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<ModalTab>(initialTab ?? 'overview');

  // Wenn Deep-Link-Tab sich ändert (neue Navigation), ebenfalls umschalten
  useEffect(() => { if (initialTab) setActiveTab(initialTab); }, [initialTab, companyId]);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ sent: number; failed: number; details: { contact: string; status: string; error?: string }[] } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // Preview / editierbare Nachrichten (pro Kontakt) + CC-Eingabefeld als Komma-String
  const [previews, setPreviews] = useState<(ExposePreview & { cc_input: string })[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Absender-Konto (Multi-Account): welches Konto wird als From genutzt?
  const [sendAccounts, setSendAccounts] = useState<SendAccount[]>([]);
  const [sendAccountId, setSendAccountId] = useState<string | null>(null);
  const [matchedLogin, setMatchedLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState<string | null>(null);
  const [confirmForeignSender, setConfirmForeignSender] = useState(false);
  const selectedAccount = sendAccounts.find(a => a.id === sendAccountId) ?? null;
  // Absender passt NICHT zum eingeloggten User → Gefahr eines Fehlversands unter fremdem Namen
  const senderForeign = !!(selectedAccount && loginEmail &&
    selectedAccount.from_email.toLowerCase() !== loginEmail.toLowerCase());
  // Konto hat kein Passwort hinterlegt → Versand physisch unmöglich
  const senderNoPassword = !!(selectedAccount && selectedAccount.has_password === false);

  useEffect(() => {
    if (!companyId) return;
    setCompany(null);
    setSendResult(null);
    setSendError(null);
    setLoading(true);
    apiClient.get(`/companies/${companyId}`)
      .then(({ data }) => {
        const c: CompanyFull = data.data;
        setCompany(c);
        // Hauptansprechpartner + Postempfänger mit E-Mail vorauswählen.
        // Hauptansprechpartner ist Empfänger (TO), die Postempfänger landen in CC.
        const preSelected = new Set<string>(
          c.contacts
            .filter(ct => (ct.is_primary_contact || ct.is_postempfaenger) && ct.email)
            .map(ct => ct.id)
        );
        setSelectedContactIds(preSelected);
      })
      .catch(() => setCompany(null))
      .finally(() => setLoading(false));
  }, [companyId]);

  const toggleContact = (id: string) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // Nach Tab-Rückkehr Company-Status frisch laden (z.B. nachdem auf Tab Stammdaten approved wurde)
  const reloadCompany = async () => {
    if (!companyId) return;
    try {
      const { data } = await apiClient.get(`/companies/${companyId}`);
      setCompany(data.data);
    } catch { /* ignore */ }
  };

  // Vorschau anfordern (Schritt 1)
  const handlePreview = async () => {
    if (selectedContactIds.size === 0) return;
    setPreviewLoading(true);
    setSendError(null);
    try {
      const [result, accountInfo] = await Promise.all([
        getExposePreview({ mandate_id: mandateId, contact_ids: [...selectedContactIds] }),
        getSendAccounts().catch(() => null),
      ]);
      setPreviews(result.data.map(p => ({ ...p, cc_input: '' })));
      if (accountInfo) {
        setSendAccounts(accountInfo.accounts);
        setMatchedLogin(accountInfo.matched_login);
        setLoginEmail(accountInfo.login_email);
        setSendAccountId(accountInfo.resolved?.id ?? accountInfo.accounts[0]?.id ?? null);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setSendError(msg ?? (err instanceof Error ? err.message : 'Fehler bei der Vorschau'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Senden (Schritt 2, aus Preview)
  const handleSend = async () => {
    if (!previews || previews.length === 0) return;
    // Keine Sendung, wenn Fehler in einer Gruppe (z.B. kein Postempfänger)
    if (previews.some(p => p.error)) {
      setSendError('Bitte Fehler in den Empfängern korrigieren, bevor versendet wird.');
      return;
    }
    // Fehlversand-Schutz: Absender passt nicht zum Login und wurde nicht bewusst bestätigt
    if (senderForeign && !confirmForeignSender) {
      setSendError('Absender passt nicht zu deinem Login. Bitte oben bewusst bestätigen oder ein passendes Konto wählen.');
      return;
    }
    setSending(true);
    setSendError(null);
    setSendResult(null);
    try {
      const messages = previews.map(p => ({
        company_id: p.company_id,
        to_email:   p.to_email,
        subject:    p.subject,
        body_text:  p.body_text,
        cc_emails:  [
          ...p.cc_emails,
          ...p.cc_input.split(',').map(s => s.trim()).filter(Boolean),
        ],
      }));
      const result = await sendExpose({
        mandate_id: mandateId,
        contact_ids: [...selectedContactIds],
        messages,
        email_account_id: sendAccountId,
      });
      setSendResult({
        sent: result.sent,
        failed: result.failed,
        details: result.data.map(r => ({ contact: r.company, status: r.status, error: r.error })),
      });
      setPreviews(null);
      if (result.sent > 0) onSent?.();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setSendError(msg ?? (err instanceof Error ? err.message : 'Fehler beim Versenden'));
    } finally {
      setSending(false);
    }
  };

  const updatePreview = (idx: number, patch: Partial<ExposePreview & { cc_input: string }>) => {
    setPreviews(prev => prev ? prev.map((p, i) => i === idx ? { ...p, ...patch } : p) : prev);
  };

  const contactsWithEmail = company?.contacts.filter(c => c.email) ?? [];
  const contactsNoEmail = company?.contacts.filter(c => !c.email) ?? [];

  return (
    <Modal
      isOpen={!!companyId}
      onClose={onClose}
      title={company?.display_name ?? 'Investor'}
      size="lg"
    >
      {loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text3)' }}>
          Lade Investorendaten...
        </div>
      )}

      {!loading && !company && (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--accent)' }}>
          Investor nicht gefunden.
        </div>
      )}

      {!loading && company && (
        <div style={{ display: 'grid', gap: '20px' }}>

          {/* ── Tabs ─────────────────────────────────────────────── */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
            {([
              { key: 'overview',      label: 'Übersicht & Versand' },
              { key: 'stammdaten',    label: 'Investor-Stammdaten' },
              { key: 'communication', label: 'Kommunikation' },
            ] as const).map(tab => (
              <button key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  // beim Verlassen Stammdaten-Tab: Status neu laden (Approve könnte passiert sein)
                  if (tab.key !== 'stammdaten') void reloadCompany();
                }}
                style={{
                  padding: '10px 18px', fontSize: '13px',
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  color: activeTab === tab.key ? 'var(--accent)' : 'var(--text3)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
                }}>
                {tab.label}
                {tab.key === 'stammdaten' && (() => {
                  const activeP = company.profiles.filter(p => p.is_active !== false);
                  const matchP = matchProfileId ? activeP.find(p => p.id === matchProfileId) : null;
                  const profileOk = matchProfileId
                    ? !!matchP?.profile_approved
                    : (activeP.length === 0 || activeP.some(p => p.profile_approved === true));
                  const needsAttention = !company.verified_at || !company.approved_at || !profileOk;
                  return needsAttention ? (
                    <span style={{ marginLeft: 6, padding: '1px 6px', fontSize: 10, background: '#F59E0B', color: '#fff', borderRadius: 8 }}>!</span>
                  ) : null;
                })()}
              </button>
            ))}
          </div>

          {activeTab === 'stammdaten' && companyId && (
            <div style={{ margin: '-4px -4px 0', maxHeight: '70vh', overflowY: 'auto' }}>
              <CompanyDetail id={companyId} />
            </div>
          )}

          {activeTab === 'communication' && companyId && (
            <MatchCommunicationTab mandateId={mandateId} companyId={companyId} />
          )}

          {activeTab === 'overview' && <>

          {/* ── Prüf-/Freigabe-Warnung mit Tab-Weiterleitung ──────── */}
          {(() => {
            const investorOk = !!company.verified_at && !!company.approved_at;
            const activeProfiles = company.profiles.filter(p => p.is_active !== false);
            // Wenn ein konkretes Match-Profil übergeben wurde: nur DIESES prüfen.
            // Sonst Fallback: irgendein aktives Profil muss freigegeben sein.
            const matchProfile = matchProfileId ? activeProfiles.find(p => p.id === matchProfileId) : null;
            const profileOk = matchProfileId
              ? !!matchProfile?.profile_approved
              : (activeProfiles.length === 0 || activeProfiles.some(p => p.profile_approved === true));
            if (investorOk && profileOk) return null;
            const profileLabel = matchProfile?.profile_name ?? 'Match-Ankaufsprofil';
            return (
              <div style={{
                padding: '14px 18px',
                background: '#fff8e1',
                border: '1px solid #F59E0B',
                borderRadius: 'var(--radius)',
                display: 'flex', alignItems: 'center', gap: '12px',
                flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, fontSize: '13px', color: '#92400e', marginBottom: '4px' }}>
                    Versand nicht möglich — Freigabe fehlt
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: '12px', color: '#92400e' }}>
                    {!company.verified_at && <li>Investor noch nicht <strong>geprüft</strong>.</li>}
                    {company.verified_at && !company.approved_at && <li>Investor noch nicht <strong>freigegeben</strong>.</li>}
                    {!profileOk && matchProfileId && (
                      <li>
                        Match-Profil <strong>„{profileLabel}"</strong> noch nicht freigegeben.
                      </li>
                    )}
                    {!profileOk && !matchProfileId && (
                      <li>
                        Kein <strong>Ankaufsprofil freigegeben</strong>
                        {activeProfiles.length > 0 && ` (${activeProfiles.length} aktive${activeProfiles.length === 1 ? 's' : ''} Profil${activeProfiles.length === 1 ? '' : 'e'}, alle ungeprüft)`}.
                      </li>
                    )}
                  </ul>
                  <div style={{ fontSize: '12px', color: '#92400e', marginTop: 4 }}>
                    Bitte Stammdaten, Kontakte und Match-Profil gewissenhaft prüfen.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('stammdaten')}
                  style={{
                    padding: '8px 16px', fontSize: 13, fontWeight: 600,
                    background: '#F59E0B', color: '#fff',
                    border: 'none', borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                  }}
                >
                  → Investor & Profil prüfen
                </button>
              </div>
            );
          })()}

          {/* ── Stammdaten ──────────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Stammdaten</span>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                {company.company_type && (
                  <div>
                    <div style={labelStyle}>Typ</div>
                    <div style={valStyle}>{company.company_type}</div>
                  </div>
                )}
                {(company.city || company.country_code) && (
                  <div>
                    <div style={labelStyle}>Standort</div>
                    <div style={valStyle}>
                      {[company.postal_code, company.city, company.country_code].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                )}
                {company.website && (
                  <div>
                    <div style={labelStyle}>Website</div>
                    <a href={company.website} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '13px', color: 'var(--info, #0077cc)' }}>
                      {company.website}
                    </a>
                  </div>
                )}
                {company.legal_name && (
                  <div>
                    <div style={labelStyle}>Rechtl. Name</div>
                    <div style={valStyle}>{company.legal_name}</div>
                  </div>
                )}
              </div>
              {company.description && (
                <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text2)', lineHeight: 1.5 }}>
                  {company.description}
                </div>
              )}
            </div>
          </div>

          {/* ── Ankaufsprofile ──────────────────────────────────── */}
          {company.profiles.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Ankaufsprofile</span>
                <span style={{ fontSize: '12px', color: 'var(--text3)' }}>{company.profiles.length}</span>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gap: '10px' }}>
                  {company.profiles.map(p => {
                    const isMatch = matchProfileId === p.id;
                    return (
                      <div key={p.id} style={{
                        padding: '12px 14px',
                        background: isMatch ? '#fff8e1' : 'var(--surface2)',
                        border: isMatch ? '2px solid #F59E0B' : '1px solid transparent',
                        borderRadius: 'var(--radius)',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr auto',
                        gap: '12px',
                        fontSize: '12px',
                        alignItems: 'center',
                      }}>
                        <div>
                          <div style={{ ...labelStyle, marginBottom: '2px' }}>
                            {isMatch ? '⭐ MATCH-PROFIL' : 'Profil'}
                          </div>
                          <div style={{ fontWeight: 500, fontSize: '13px' }}>{p.profile_name ?? 'Ankaufsprofil'}</div>
                          {!p.is_active && <span style={{ fontSize: '10px', color: 'var(--text3)' }}>Inaktiv</span>}
                        </div>
                        <div>
                          <div style={labelStyle}>Volumen</div>
                          <div>{formatVolume(p.volume_min_eur, p.volume_max_eur)}</div>
                        </div>
                        <div>
                          <div style={labelStyle}>Länder</div>
                          <div>{p.target_countries?.join(', ') || '—'}</div>
                        </div>
                        <div>
                          {p.profile_approved ? (
                            <span style={{ padding: '3px 9px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>✓ freigegeben</span>
                          ) : (
                            <span style={{ padding: '3px 9px', background: '#fff3e0', color: '#e65100', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>⏳ ungeprüft</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Kontakte + Exposé-Auswahl ───────────────────────── */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Exposé versenden an</span>
              <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
                {selectedContactIds.size} ausgewählt
              </span>
            </div>
            <div className="card-body">
              {previews ? (
                /* ── Vorschau & Bearbeiten ────────────────────────── */
                <div style={{ display: 'grid', gap: '14px' }}>
                  <div style={{ padding: '10px 14px', background: '#fffbf5', borderLeft: '3px solid var(--accent)',
                    borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--text2)' }}>
                    Prüfe und passe die E-Mails an, bevor sie versendet werden. Weitere Empfänger als <code>CC</code> kommaseparat hinzufügen.
                  </div>

                  {/* ── Absender-Konto (From) ─────────────────────────── */}
                  <div style={{
                    padding: '12px 14px', background: 'var(--surface2)',
                    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                    display: 'grid', gap: '8px',
                  }}>
                    <label style={labelStyle}>Absender (From) — so erscheint die E-Mail beim Empfänger</label>
                    {sendAccounts.length > 0 ? (
                      <>
                        <select
                          value={sendAccountId ?? ''}
                          onChange={e => { setSendAccountId(e.target.value || null); setConfirmForeignSender(false); }}
                          style={{
                            width: '100%', padding: '7px 9px', fontSize: '12px',
                            fontFamily: 'DM Mono, monospace',
                            border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
                            background: 'var(--surface)', color: 'var(--text)',
                          }}>
                          {sendAccounts.map(a => (
                            <option key={a.id} value={a.id}>
                              {(a.from_name ? a.from_name + ' ' : '')}&lt;{a.from_email}&gt;{a.is_default ? ' — Standard' : ''} · {a.label}
                            </option>
                          ))}
                        </select>
                        {selectedAccount && (
                          <div style={{ fontSize: '12px', color: 'var(--text2)', fontFamily: 'DM Mono, monospace' }}>
                            📤 {selectedAccount.from_name ? `"${selectedAccount.from_name}" ` : ''}&lt;{selectedAccount.from_email}&gt;
                          </div>
                        )}
                        {senderNoPassword && (
                          <div style={{ fontSize: '12px', color: '#b00020', background: '#ffe9e9',
                            border: '1px solid #e57373', borderRadius: 'var(--radius)', padding: '9px 11px' }}>
                            ⛔ <strong>Für {selectedAccount?.from_email} ist kein Passwort hinterlegt</strong> — Versand nicht möglich.
                            Trage das Postfach-Passwort unter <strong>Einstellungen → Mail-Konten</strong> ein (IMAP-Feld leer lassen = SMTP-Passwort wird genutzt).
                          </div>
                        )}
                        {senderForeign && (
                          <div style={{ fontSize: '12px', color: '#b00020', background: '#ffe9e9',
                            border: '1px solid #e57373', borderRadius: 'var(--radius)', padding: '9px 11px',
                            display: 'grid', gap: '7px' }}>
                            <div>
                              ⛔ <strong>Absender passt nicht zu deinem Login.</strong> Du bist als <strong>{loginEmail}</strong> angemeldet,
                              die E-Mail würde aber unter <strong>{selectedAccount?.from_email}</strong> versendet.
                              {!matchedLogin && <> Für dein Login gibt es kein aktives eigenes Mail-Konto — hinterlege dein Passwort unter <strong>Einstellungen → Mail-Konten</strong>.</>}
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontWeight: 500 }}>
                              <input type="checkbox" checked={confirmForeignSender}
                                onChange={e => setConfirmForeignSender(e.target.checked)} />
                              Ich sende diese E-Mail bewusst unter <span style={{ fontFamily: 'DM Mono, monospace' }}>{selectedAccount?.from_email}</span>.
                            </label>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '12px', color: 'var(--text3)' }}>
                        Kein Mail-Konto konfiguriert — es wird die SMTP-Einstellung aus <strong>Einstellungen</strong> genutzt.
                      </div>
                    )}
                  </div>
                  {previews.map((p, idx) => (
                    <div key={p.company_id} style={{
                      padding: '14px 16px',
                      background: p.error ? '#fff4f4' : 'var(--surface2)',
                      borderRadius: 'var(--radius)',
                      border: p.error ? '1px solid #e57373' : '1px solid var(--border)',
                      display: 'grid', gap: '10px',
                    }}>
                      {/* Kopfzeile: Firma + Postempfänger */}
                      <div style={{ fontSize: '12px', color: 'var(--text3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <div>
                          <strong style={{ fontSize: '13px', color: 'var(--text)' }}>{p.company_name ?? 'Investor'}</strong>
                          {p.postempfaenger_name && (
                            <span style={{ marginLeft: '8px', color: 'var(--text3)' }}>
                              · Postempfänger: {p.postempfaenger_name}
                            </span>
                          )}
                        </div>
                        {p.cc_emails.length > 0 && (
                          <span style={{ fontSize: '11px', color: 'var(--text3)' }}>
                            {p.cc_emails.length} ausgewählte Kontakte als CC
                          </span>
                        )}
                      </div>

                      {p.error ? (
                        <div style={{ padding: '10px 12px', background: '#ffe0e0', color: '#b00020',
                          borderRadius: 'var(--radius)', fontSize: '12px' }}>
                          ⚠️ {p.error}
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                              <label style={labelStyle}>An (Postempfänger)</label>
                              <input type="email" value={p.to_email}
                                onChange={e => updatePreview(idx, { to_email: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px', fontFamily: 'DM Mono, monospace',
                                  border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)' }} />
                            </div>
                            <div>
                              <label style={labelStyle}>Zusätzliche CC (kommaseparat)</label>
                              <input type="text" value={p.cc_input}
                                onChange={e => updatePreview(idx, { cc_input: e.target.value })}
                                placeholder="weitere@empfaenger.de, …"
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px', fontFamily: 'DM Mono, monospace',
                                  border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)' }} />
                            </div>
                          </div>

                          {/* Vorgewählte CC-Adressen aus Kontaktauswahl */}
                          {p.cc_emails.length > 0 && (
                            <div>
                              <label style={labelStyle}>CC aus Auswahl</label>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {p.cc_emails.map(e => (
                                  <span key={e} style={{
                                    padding: '3px 9px', borderRadius: '10px', fontSize: '11px',
                                    background: 'var(--surface)', border: '1px solid var(--border)',
                                    fontFamily: 'DM Mono, monospace', color: 'var(--text2)',
                                  }}>{e}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div>
                            <label style={labelStyle}>Betreff</label>
                            <input value={p.subject}
                              onChange={e => updatePreview(idx, { subject: e.target.value })}
                              style={{ width: '100%', padding: '6px 8px', fontSize: '12px',
                                border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)' }} />
                          </div>
                          <div>
                            <label style={labelStyle}>Text</label>
                            <textarea value={p.body_text}
                              onChange={e => updatePreview(idx, { body_text: e.target.value })}
                              rows={10}
                              style={{ width: '100%', padding: '8px 10px', fontSize: '12px', lineHeight: 1.6,
                                fontFamily: 'inherit', resize: 'vertical',
                                border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)' }} />
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {sendError && (
                    <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px' }}>
                      ⚠️ {sendError}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button type="button" onClick={() => setPreviews(null)}
                      style={{ padding: '7px 14px', background: 'var(--surface)', border: '1px solid var(--border2)',
                        borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer', color: 'var(--text2)' }}>
                      ← Zurück
                    </button>
                    <button type="button" onClick={handleSend}
                      disabled={sending || senderNoPassword || (senderForeign && !confirmForeignSender)}
                      title={senderNoPassword ? 'Für dieses Konto ist kein Passwort hinterlegt.'
                        : (senderForeign && !confirmForeignSender ? 'Absender passt nicht zu deinem Login — bitte oben bewusst bestätigen.' : '')}
                      style={{
                        padding: '9px 22px', fontSize: '13px', fontWeight: 600,
                        background: (senderNoPassword || (senderForeign && !confirmForeignSender)) ? 'var(--border2)' : 'var(--accent)',
                        color: '#fff',
                        border: 'none', borderRadius: 'var(--radius)',
                        cursor: sending ? 'wait' : ((senderNoPassword || (senderForeign && !confirmForeignSender)) ? 'not-allowed' : 'pointer'),
                        opacity: sending ? 0.7 : 1,
                      }}>
                      {sending ? '⏳ Wird versendet …' : `✉ Jetzt versenden (${previews.length})`}
                    </button>
                  </div>
                </div>
              ) : sendResult ? (
                /* Versende-Ergebnis */
                <div style={{ display: 'grid', gap: '8px' }}>
                  <div style={{
                    padding: '12px 16px', borderRadius: 'var(--radius)', marginBottom: '8px',
                    background: sendResult.failed === 0 ? '#e8f5e9' : '#fff3e0',
                    color: sendResult.failed === 0 ? '#2e7d32' : '#e65100',
                    fontSize: '13px', fontWeight: 500,
                  }}>
                    {sendResult.failed === 0
                      ? `✓ Alle ${sendResult.sent} E-Mails erfolgreich versendet.`
                      : `${sendResult.sent} versendet · ${sendResult.failed} fehlgeschlagen`}
                  </div>
                  {sendResult.details.map((d, i) => (
                    <div key={i} style={{ fontSize: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span>{d.status === 'sent' ? '✓' : '✗'}</span>
                      <span style={{ color: d.status === 'sent' ? 'var(--text2)' : 'var(--accent)' }}>
                        {d.contact}{d.error ? ` – ${d.error}` : ''}
                      </span>
                    </div>
                  ))}
                  <button onClick={() => setSendResult(null)}
                    style={{ marginTop: '8px', padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}>
                    Neue Auswahl
                  </button>
                </div>
              ) : (
                <>
                  {company.contacts.length === 0 && (
                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>
                      Keine Kontakte hinterlegt.
                    </div>
                  )}

                  {/* Kontakte mit E-Mail */}
                  {contactsWithEmail.length > 0 && (
                    <div style={{ display: 'grid', gap: '6px', marginBottom: contactsNoEmail.length > 0 ? '12px' : 0 }}>
                      {contactsWithEmail.map(c => (
                        <div key={c.id} style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 12px', borderRadius: 'var(--radius)',
                          background: selectedContactIds.has(c.id) ? 'var(--accent-light)' : 'var(--surface2)',
                          border: '1px solid',
                          borderColor: selectedContactIds.has(c.id) ? 'var(--accent)' : 'var(--border)',
                          transition: 'all 0.1s',
                        }}>
                          <input
                            type="checkbox"
                            checked={selectedContactIds.has(c.id)}
                            onChange={() => toggleContact(c.id)}
                            style={{ accentColor: 'var(--accent)', width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                          />
                          <label
                            onClick={() => toggleContact(c.id)}
                            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                          >
                            <div style={{ fontSize: '13px', fontWeight: 500, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              {c.display_name}
                              {c.is_postempfaenger && (
                                <span style={{ fontSize: '10px', color: 'var(--info, #0077cc)', fontWeight: 500 }}>
                                  ✉ Postempfänger
                                </span>
                              )}
                              {c.is_primary_contact && (
                                <span style={{ fontSize: '10px', color: 'var(--accent)', fontWeight: 500 }}>
                                  Haupt-AP
                                </span>
                              )}
                            </div>
                            {c.job_title && (
                              <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '1px' }}>
                                {c.job_title}
                              </div>
                            )}
                            {c.email && (
                              <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: '2px', fontFamily: 'DM Mono, monospace' }}>
                                {c.email}
                              </div>
                            )}
                          </label>
                          <a
                            href={`/contacts/${c.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            title="Kontakt öffnen"
                            style={{
                              fontSize: '14px', color: 'var(--text3)', textDecoration: 'none',
                              padding: '4px 6px', borderRadius: '4px', flexShrink: 0,
                              display: 'flex', alignItems: 'center',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
                            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}
                          >
                            ↗
                          </a>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Kontakte ohne E-Mail (nicht auswählbar) */}
                  {contactsNoEmail.length > 0 && (
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                        Keine E-Mail hinterlegt
                      </div>
                      {contactsNoEmail.map(c => (
                        <div key={c.id} style={{
                          padding: '8px 12px', fontSize: '12px', color: 'var(--text3)',
                          background: 'var(--surface2)', borderRadius: 'var(--radius)',
                          marginBottom: '4px',
                          display: 'flex', alignItems: 'center', gap: '8px',
                        }}>
                          <span style={{ opacity: 0.4 }}>☐</span>
                          <span style={{ flex: 1 }}>{c.display_name}{c.job_title ? ` · ${c.job_title}` : ''}</span>
                          <a
                            href={`/contacts/${c.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Kontakt öffnen"
                            style={{ fontSize: '13px', color: 'var(--text3)', textDecoration: 'none', flexShrink: 0 }}
                            onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
                            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}
                          >
                            ↗
                          </a>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Fehlermeldung */}
                  {sendError && (
                    <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginTop: '12px' }}>
                      ⚠️ {sendError}
                    </div>
                  )}

                  {/* Aktionen */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedContactIds(new Set(contactsWithEmail.map(c => c.id)))}
                        style={{ padding: '5px 12px', fontSize: '12px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text2)' }}
                      >
                        Alle auswählen
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedContactIds(new Set())}
                        style={{ padding: '5px 12px', fontSize: '12px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text2)' }}
                      >
                        Keine
                      </button>
                    </div>

                    {(() => {
                      const activeP = company.profiles.filter(p => p.is_active !== false);
                      const matchP = matchProfileId ? activeP.find(p => p.id === matchProfileId) : null;
                      const profileOk = matchProfileId
                        ? !!matchP?.profile_approved
                        : (activeP.length === 0 || activeP.some(p => p.profile_approved === true));
                      const investorOk = !!company.verified_at && !!company.approved_at;
                      const blocked = !investorOk || !profileOk;
                      const blockReason = !investorOk
                        ? 'Investor muss erst geprüft und freigegeben werden'
                        : !profileOk
                          ? (matchProfileId ? `Match-Profil „${matchP?.profile_name ?? '—'}" muss freigegeben werden` : 'Mindestens ein Ankaufsprofil muss freigegeben werden')
                          : undefined;
                      const disabled = previewLoading || selectedContactIds.size === 0 || blocked;
                      return (
                        <button
                          type="button"
                          onClick={handlePreview}
                          disabled={disabled}
                          title={blockReason}
                          style={{
                            padding: '9px 22px', fontSize: '13px', fontWeight: 600,
                            background: (selectedContactIds.size === 0 || blocked) ? 'var(--surface2)' : 'var(--accent)',
                            color: (selectedContactIds.size === 0 || blocked) ? 'var(--text3)' : '#fff',
                            border: 'none', borderRadius: 'var(--radius)',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: previewLoading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: '8px',
                          }}
                        >
                          {previewLoading ? (
                            <>⏳ Vorschau…</>
                          ) : blocked ? (
                            <>🔒 Versand gesperrt</>
                          ) : (
                            <>✉ Vorschau & versenden ({selectedContactIds.size})</>
                          )}
                        </button>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>

          </>}
        </div>
      )}
    </Modal>
  );
}
